import React from "react";

// Source
import StoreRoute from "../../store/Route";
import StoreRole from "../../store/Role";
import StoreDocumentPreview from "../../store/custom/Document";
import StoreLocalization from "../../store/Localization";
import StorePreference from "../../store/Preference";
import ControllerAlert from "../Alert";
import * as HelperSrc from "../../HelperSrc";
import * as ApiDocument from "../../api/custom/Document";
import * as ModelPagination from "../../model/Pagination";
import * as ModelDocument from "../../model/custom/Document";
import * as ModelRoute from "../../model/Route";
import * as ModelCustomRoute from "../../model/custom/Route";
import * as ModelRole from "../../model/Role";
import * as ModelLocalization from "../../model/Localization";
import * as ModelCustomLocalization from "../../model/custom/Localization";
import * as ModelPreference from "../../model/Preference";
import * as ModelCustomPreference from "../../model/custom/Preference";
import * as ModelHelperSrc from "../../model/HelperSrc";

export default class ControllerDocumentList {
    // Variable
    private storeRoute: ModelRoute.Istore<ModelCustomRoute.Idata>;
    private storageRouteData = React.useRef<ModelCustomRoute.Idata | undefined>(undefined);
    private storeRole: ModelRole.Istore;
    private storageRoleData = React.useRef<ModelRole.Idata | undefined>(undefined);
    private storeLocalization: ModelLocalization.Istore<ModelCustomLocalization.Idata>;
    private storageLocalizationData = React.useRef<ModelCustomLocalization.Idata | undefined>(undefined);
    private storeDocumentPreview: ModelDocument.Istore;
    private storageDocumentPreviewData = React.useRef<ModelDocument.Idata | undefined>(undefined);
    private storePreference: ModelPreference.Istore<ModelCustomPreference.Idata>;
    private storagePreferenceData = React.useRef<ModelCustomPreference.Idata | undefined>(undefined);

    private controllerAlert: ControllerAlert;

    private elementClientName = React.useRef<HTMLInputElement | null>(null);
    private elementClientIndustry = React.useRef<HTMLInputElement | null>(null);
    private elementProjectName = React.useRef<HTMLInputElement | null>(null);
    private elementReportingYear = React.useRef<HTMLInputElement | null>(null);
    private elementTemplateLanguage = React.useRef<HTMLInputElement | null>(null);
    private elementLatestUpdate = React.useRef<HTMLInputElement | null>(null);
    private elementLatestUpdateById = React.useRef<HTMLInputElement | null>(null);
    private elementViewMain = React.useRef<HTMLElement | null>(null);

    private isRendered = React.useRef<boolean>(false);
    private idRemove = React.useRef(0);
    private filter = React.useRef<ModelPagination.CustomPagination.IfilterDocument | undefined>(undefined);
    private dataMainList = React.useRef<ModelDocument.Idata[] | undefined>(undefined);
    private language = React.useRef<string>("");

    getStorageRole: ModelRole.Idata | undefined;

    getApiLoadingList: Record<string, boolean>;
    setApiLoadingList: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;

    getSelectOption: ModelDocument.IselectOption | undefined;
    setSelectOption: React.Dispatch<React.SetStateAction<ModelDocument.IselectOption | undefined>>;

    getIsDialogOpen: boolean;
    setIsDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;

    getPagination: ModelPagination.Idata | undefined;
    setPagination: React.Dispatch<React.SetStateAction<ModelPagination.Idata | undefined>>;

    getFilter: ModelPagination.CustomPagination.IfilterDocument | undefined;
    setFilter: React.Dispatch<React.SetStateAction<ModelPagination.CustomPagination.IfilterDocument | undefined>>;

    getDataMainList: ModelDocument.Idata[] | undefined;
    setDataMainList: React.Dispatch<React.SetStateAction<ModelDocument.Idata[] | undefined>>;

    getLanguage: string;
    setLanguage: React.Dispatch<React.SetStateAction<string>>;

    getStorageDocumentPreview: ModelDocument.Idata | undefined;
    setStorageDocumentPreview: React.Dispatch<React.SetStateAction<ModelDocument.Idata | undefined>>;

    getIsDraftDialogOpen: boolean;
    setIsDraftDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;

    getElementClientIndustryValue: string;
    setElementClientIndustryValue: React.Dispatch<React.SetStateAction<string>>;

    getElementLatestUpdateByIdValue: string;
    setElementLatestUpdateByIdValue: React.Dispatch<React.SetStateAction<string>>;

    // Method
    constructor(isRendered: boolean) {
        this.storeRoute = new StoreRoute();
        this.storageRouteData.current = this.storeRoute.storageGet();
        this.storeRole = new StoreRole();
        this.storageRoleData.current = this.storeRole.storageGet();
        this.storeLocalization = new StoreLocalization();
        this.storageLocalizationData.current = this.storeLocalization.storageGet();
        this.storeDocumentPreview = new StoreDocumentPreview();
        this.storageDocumentPreviewData.current = this.storeDocumentPreview.storageGet();
        this.storePreference = new StorePreference();
        this.storagePreferenceData.current = this.storePreference.storageGet();

        this.controllerAlert = new ControllerAlert(isRendered);

        this.isRendered.current = isRendered;

        const [getStorageRole] = React.useState<ModelRole.Idata | undefined>(this.storageRoleData.current);
        this.getStorageRole = getStorageRole;

        const [getApiLoadingList, setApiLoadingList] = React.useState<Record<string, boolean>>({});
        this.getApiLoadingList = getApiLoadingList;
        this.setApiLoadingList = setApiLoadingList;

        const [getSelectOption, setSelectOption] = React.useState<ModelDocument.IselectOption | undefined>(undefined);
        this.getSelectOption = getSelectOption;
        this.setSelectOption = setSelectOption;

        const [getIsDialogOpen, setIsDialogOpen] = React.useState(false);
        this.getIsDialogOpen = getIsDialogOpen;
        this.setIsDialogOpen = setIsDialogOpen;

        const [getPagination, setPagination] = React.useState<ModelPagination.Idata | undefined>(undefined);
        this.getPagination = getPagination;
        this.setPagination = setPagination;

        const [getFilter, setFilter] = React.useState<ModelPagination.CustomPagination.IfilterDocument | undefined>(this.filter.current);
        this.getFilter = getFilter;
        this.setFilter = setFilter;

        const [getDataMainList, setDataMainList] = React.useState<ModelDocument.Idata[] | undefined>(this.dataMainList.current);
        this.getDataMainList = getDataMainList;
        this.setDataMainList = setDataMainList;

        const [getLanguage, setLanguage] = React.useState<string>(this.language.current);
        this.getLanguage = getLanguage;
        this.setLanguage = setLanguage;

        const [getStorageDocumentPreview, setStorageDocumentPreview] = React.useState<ModelDocument.Idata | undefined>(
            this.storageDocumentPreviewData.current
        );
        this.getStorageDocumentPreview = getStorageDocumentPreview;
        this.setStorageDocumentPreview = setStorageDocumentPreview;

        const [getIsDraftDialogOpen, setIsDraftDialogOpen] = React.useState(false);
        this.getIsDraftDialogOpen = getIsDraftDialogOpen;
        this.setIsDraftDialogOpen = setIsDraftDialogOpen;

        const [getElementClientIndustryValue, setElementClientIndustryValue] = React.useState<string>("");
        this.getElementClientIndustryValue = getElementClientIndustryValue;
        this.setElementClientIndustryValue = setElementClientIndustryValue;

        const [getElementLatestUpdateByIdValue, setElementLatestUpdateByIdValue] = React.useState<string>("");
        this.getElementLatestUpdateByIdValue = getElementLatestUpdateByIdValue;
        this.setElementLatestUpdateByIdValue = setElementLatestUpdateByIdValue;
    }

    initializeHook = (): void => {
        React.useEffect(() => {
            HelperSrc.domObserver(".view_document_list", () => {
                this.initializeHtmlElement();
            });

            this.apiSelectOption();

            this.apiDataList();
        }, []);

        React.useEffect(() => {
            this.storageRouteData.current = this.storeRoute.storageGet();
            this.storageRoleData.current = this.storeRole.storageGet();
            this.storageLocalizationData.current = this.storeLocalization.storageGet();
            this.storageDocumentPreviewData.current = this.storeDocumentPreview.storageGet();
        }, [
            this.storageRouteData.current,
            this.storageRoleData.current,
            this.storageLocalizationData.current,
            this.storageDocumentPreviewData.current
        ]);

        React.useEffect(() => {
            this.storagePreferenceData.current = this.storePreference.storageGet();

            if (this.storagePreferenceData.current) {
                const initialLanguage = this.language.current;

                if (this.language.current !== this.storagePreferenceData.current.language) {
                    this.language.current = this.storagePreferenceData.current.language;
                    this.setLanguage(this.language.current);

                    if (initialLanguage !== "") {
                        this.apiSelectOption();
                    }
                }
            }
        }, [this.storagePreferenceData.current]);
    };

    apiDataList = (isFilter = false): void => {
        this.filterParameter(isFilter);

        if (this.filter.current) {
            this.setApiLoadingList((prev) => ({
                ...prev,
                dataList: true
            }));

            ApiDocument.dataList(this.filter.current, this.filter.current.sort_order, this.filter.current.page, (response) => {
                this.setApiLoadingList((prev) => ({
                    ...prev,
                    dataList: false
                }));

                if (response instanceof Error || response.message.error) {
                    if (response instanceof Error) {
                        HelperSrc.writeLog("error", response.message);
                    } else {
                        this.controllerAlert.show("error", response.message.error, false);
                    }

                    return;
                }

                this.setPagination(response.pagination);

                this.dataMainList.current = response.pagination.data as unknown as ModelDocument.Idata[];
                this.setDataMainList(this.dataMainList.current);

                this.controllerAlert.show("success", response.message.success);
            });
        }
    };

    clickCreate = (): void => {
        if (this.storageRouteData.current) {
            HelperSrc.routerHistoryPush(this.storageRouteData.current.documentCreate.path);
        }
    };

    clickFilterApply = (): void => {
        this.apiDataList(true);
    };

    clickItem = (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>, data: ModelDocument.Idata): void => {
        const element = event.target as HTMLElement;
        const tagName = element.tagName;

        if (this.storageRouteData.current && tagName !== "path") {
            HelperSrc.routerHistoryPush(this.storageRouteData.current.documentPreview.path, data as unknown as Record<string, unknown>);
        }
    };

    clickRemove = (event: React.MouseEvent<SVGSVGElement, MouseEvent>, id: string): void => {
        event.stopPropagation();

        this.idRemove.current = parseInt(id);

        this.setIsDialogOpen(true);
    };

    clickDialogRemoveConfirm = (): void => {
        if (this.idRemove.current > 0) {
            this.setApiLoadingList((prev) => ({
                ...prev,
                removeData: true
            }));

            ApiDocument.removeData(this.idRemove.current, (response) => {
                this.setApiLoadingList((prev) => ({
                    ...prev,
                    removeData: false
                }));

                this.setIsDialogOpen(false);

                if (response instanceof Error || response.message.error) {
                    if (response instanceof Error) {
                        HelperSrc.writeLog("error", response.message);
                    } else {
                        this.controllerAlert.show("error", response.message.error, false);
                    }

                    return;
                }

                this.controllerAlert.show("success", response.message.success);

                this.apiDataList();
            });
        }
    };

    clickDialogRemoveCancel = (): void => {
        this.setIsDialogOpen(false);
    };

    clickDraftItem = (): void => {
        if (this.storageRouteData.current) {
            HelperSrc.routerHistoryPush(this.storageRouteData.current.documentPreview.path, {
                temporaryData: this.storageDocumentPreviewData.current
            });
        }
    };

    clickDraftRemove = (event: React.MouseEvent<SVGSVGElement, MouseEvent>): void => {
        event.stopPropagation();

        this.setIsDraftDialogOpen(true);
    };

    clickDialogDraftRemoveConfirm = (): void => {
        this.setStorageDocumentPreview(undefined);

        this.storeDocumentPreview.storageRemove();

        const storagePreferenceData = {
            ...this.storagePreferenceData.current,
            isShowPreviewDraftDialog: true,
            isAutoSaveDraft: true
        } as ModelCustomPreference.Idata;

        this.storePreference.storageSet(storagePreferenceData);

        this.setIsDraftDialogOpen(false);
    };

    clickDialogDraftRemoveCancel = (): void => {
        this.setIsDraftDialogOpen(false);
    };

    private initializeHtmlElement = (): void => {
        if (!this.elementViewMain.current) {
            this.elementClientName.current = document.querySelector<HTMLInputElement>("[name='client_name']");
            this.elementClientIndustry.current = document.querySelector<HTMLInputElement>("[name='client_industry']");
            this.elementProjectName.current = document.querySelector<HTMLInputElement>("[name='project_name']");
            this.elementReportingYear.current = document.querySelector<HTMLInputElement>("[name='reporting_year']");
            this.elementTemplateLanguage.current = document.querySelector<HTMLInputElement>("[name='template_language']");
            this.elementLatestUpdate.current = document.querySelector<HTMLInputElement>("[name='latest_update']");
            this.elementLatestUpdateById.current = document.querySelector<HTMLInputElement>("[name='latest_update_by_id']");
            this.elementViewMain.current = document.querySelector<HTMLElement>(".view_document_list");
        }
    };

    private apiSelectOption = (): void => {
        this.setApiLoadingList((prev) => ({
            ...prev,
            selectOption: true
        }));

        ApiDocument.selectOption((response) => {
            this.setApiLoadingList((prev) => ({
                ...prev,
                selectOption: false
            }));

            if (response instanceof Error || response.message.error) {
                if (response instanceof Error) {
                    HelperSrc.writeLog("error", response.message);
                } else {
                    this.controllerAlert.show("error", response.message.error, false);
                }

                return;
            }

            this.setSelectOption(response.data.result as unknown as ModelDocument.IselectOption);
        });
    };

    private filterParameter = (isFilter: boolean): void => {
        const pageUrl = HelperSrc.urlVariable("page") && !isFilter ? HelperSrc.urlVariable("page") : "1";
        const sortOrderUlr = HelperSrc.urlVariable("sort_order") ? HelperSrc.urlVariable("sort_order") : "desc";
        const projectNameUrl = HelperSrc.urlVariable("project_name") ? HelperSrc.urlVariable("project_name") : "";
        const clientNameUrl = HelperSrc.urlVariable("client_name") ? HelperSrc.urlVariable("client_name") : "";
        const clientIndustryUrl = HelperSrc.urlVariable("client_industry") ? HelperSrc.urlVariable("client_industry") : "";
        const reportingYearUrl = HelperSrc.urlVariable("reporting_year") ? HelperSrc.urlVariable("reporting_year") : "";
        const templateLanguageUrl = HelperSrc.urlVariable("template_language") ? HelperSrc.urlVariable("template_language") : "";
        const latestUpdateUrl = HelperSrc.urlVariable("latest_update") && !isFilter ? HelperSrc.urlVariable("latest_update") : "";
        const latestUpdateByIdUrl = HelperSrc.urlVariable("latest_update_by_id") && !isFilter ? HelperSrc.urlVariable("latest_update_by_id") : "";

        const projectNameResult =
            this.elementProjectName.current && this.elementProjectName.current.value !== projectNameUrl
                ? this.elementProjectName.current.value
                : projectNameUrl;
        const clientNameResult =
            this.elementClientName.current && this.elementClientName.current.value !== clientNameUrl
                ? this.elementClientName.current.value
                : clientNameUrl;
        const clientIndustryResult =
            this.elementClientIndustry.current && this.elementClientIndustry.current.value !== clientIndustryUrl
                ? this.elementClientIndustry.current.value
                : clientIndustryUrl;
        const reportingYearResult =
            this.elementReportingYear.current && this.elementReportingYear.current.value !== reportingYearUrl
                ? this.elementReportingYear.current.value
                : reportingYearUrl;
        const templateLanguageResult =
            this.elementTemplateLanguage.current && this.elementTemplateLanguage.current.value !== templateLanguageUrl
                ? this.elementTemplateLanguage.current.value
                : templateLanguageUrl;
        const latestUpdateResult =
            this.elementLatestUpdate.current && this.elementLatestUpdate.current.value !== latestUpdateUrl
                ? this.elementLatestUpdate.current.value
                : latestUpdateUrl;
        const latestUpdateByIdResult =
            this.elementLatestUpdateById.current && this.elementLatestUpdateById.current.value !== latestUpdateByIdUrl
                ? this.elementLatestUpdateById.current.value
                : latestUpdateByIdUrl;

        this.filter.current = {
            page: pageUrl,
            sort_order: sortOrderUlr,
            project_name: projectNameResult as string & ModelHelperSrc.IvalidationForm,
            client_name: clientNameResult as string & ModelHelperSrc.IvalidationForm,
            client_industry: clientIndustryResult as string & ModelHelperSrc.IvalidationForm,
            reporting_year: reportingYearResult as string & ModelHelperSrc.IvalidationForm,
            template_language: templateLanguageResult as string & ModelHelperSrc.IvalidationForm,
            latest_update: latestUpdateResult as string & ModelHelperSrc.IvalidationForm,
            latest_update_by_id: latestUpdateByIdResult as unknown as number & ModelHelperSrc.IvalidationForm
        } as ModelPagination.CustomPagination.IfilterDocument;
        this.setFilter(this.filter.current);

        if (this.storageRouteData.current) {
            HelperSrc.routerHistoryPush(
                `${this.storageRouteData.current.documentList.path}?` +
                    `page=${this.filter.current.page}&` +
                    `sort_order=${this.filter.current.sort_order}&` +
                    `project_name=${this.filter.current.project_name}&` +
                    `client_name=${this.filter.current.client_name}&` +
                    `client_industry=${this.filter.current.client_industry}&` +
                    `reporting_year=${this.filter.current.reporting_year}&` +
                    `template_language=${this.filter.current.template_language}&` +
                    `latest_update=${this.filter.current.latest_update}&` +
                    `latest_update_by_id=${this.filter.current.latest_update_by_id}`,
                {},
                "",
                true
            );
        }
    };
}
